/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2023 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import React, { useEffect, useState } from 'react'
import Link from "next/link";
import Image from 'next/image';
import { Button } from '@adobe/react-spectrum';
import useLongPress from '@/utils/long-press';

import style from './image-component.module.scss'
import QRModal from '@/components/QRModal/Modal';
import { sendAdobeAnalyticsTryNowEvent } from '@/utils/utils';
import { generateTitleAndDescription } from '@/utils/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { doEncryption } from "@/utils/encryption";

export default function ImageComponent({ item, category, locale }: any) {
    const router = useRouter();
    const [modal, setModal] = React.useState(false);
    const [url, setURL] = React.useState(item.url);
    const [afterUrl, setAfterURL] = React.useState(item.after_url);
    const t = useTranslations("");
    const [altText, setAltText] = useState('Grid image');
    const [error, setError] = useState(false);

    useEffect(() => {
        const { description } = generateTitleAndDescription(item);
        setAltText(description);
        setURL(item.url);
        setAfterURL(item.after_url);
    }, [locale, item, category])

    const handleModal = () => {
        const userAgent = navigator.userAgent;
        if (/iPad|iPhone|iPod/i.test(userAgent)) {
            window.location.href = "https://photoshopexpress.app.link/kaSD4e2tMGb";
        }
        else if (/android/i.test(userAgent)) {
            window.location.href = "https://photoshopexpress.app.link/kaSD4e2tMGb";
        } else
            setModal(!modal);
    }

    const handleTryNow = () => {
        const initiatingSource = 'HomeScreen';
        sendAdobeAnalyticsTryNowEvent(initiatingSource);
        handleModal();
    }

    const handleImageLoadingError = () => {
        setError(true);
    }

    if (!item || !url || !afterUrl) return null;

    const onLongPress = () => {
        console.log('longpress is triggered');
    };

    const onClick = () => {
        return router.push(`/${item.locale}/discover/category/${category?category:'all'}/${item.id}`)
    }

    const defaultOptions = {
        shouldPreventDefault: true,
        delay: 500,
    };
    const longPressEvent = useLongPress(onLongPress, onClick, defaultOptions);

    let encryptedGuid = null;
    if (item?.owner_id) {
        encryptedGuid = doEncryption(item.owner_id);
    }


    return <div className={style.gridItem}>
        <Link href={`/${item.locale}/user/${encryptedGuid}`} className={style.link}>
            <div className={style.profile}>
                <Image width={40} height={40} alt="Profile image" src={item.owner} loading="eager" blurDataURL="@/assets/psx.png" />
            </div>
        </Link>
        <Link onContextMenu={e => e.preventDefault()} href={`/${item.locale}/discover/category/${category?category:'all'}/${item.id}`} className={style.link} {...longPressEvent}>
            <Image width={512} height={512} src={url} alt={altText} className={`${style.image}`} placeholder="blur" blurDataURL="@/assets/psx.png" />
            {!error && <Image onError={handleImageLoadingError} width={512} height={512} src={afterUrl} alt={''} className={`${style.image} ${style.afterImg}`} />}
        </Link>
        <Button UNSAFE_className={`${style.tryBtn} cursor-pointer`} variant="negative" position={'absolute'} bottom={'size-150'} right={'size-100'} onPress={handleTryNow}>{t("try_now")}</Button>
        <QRModal open={modal} setOpen={setModal} lang={locale} />
    </div>
}   